import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Cell, Pie, PieChart, ResponsiveContainer } from "recharts";
import { cn } from "@nexus/ui";
import type {
  InsightsBandSplit,
  PreflightScoreBand,
} from "../../types/preflight.types";
import { TONE_BG, TONE_TEXT } from "../../constants/result-state";
import { bandTone, type BandTone } from "../../lib/score-band";
import { canAnimate } from "../../lib/motion";
import { AnimatedNumber } from "../AnimatedNumber/AnimatedNumber";
import { WidgetHelp } from "../WidgetHelp/WidgetHelp";

/** Legend and slice order: best band first, matching the score rail's reading order. */
const DONUT_BANDS: PreflightScoreBand[] = ["STRONG", "NEEDS_REVIEW", "AT_RISK"];

/**
 * SVG fills cannot use Tailwind classes, so the band tones are resolved to the same
 * design tokens the `TONE_BG` classes compile to — the donut and the badges stay in sync.
 */
const TONE_CHART_FILL: Record<BandTone, string> = {
  success: "var(--success)",
  warning: "var(--warning)",
  error: "var(--error)",
};

/** A 26px ring on the 132px (`size-33`) donut, expressed as a share of the radius. */
const DONUT_INNER_RADIUS = "61%";
const DONUT_OUTER_RADIUS = "100%";

/**
 * Rounds every band's ends while any band is highlighted, so the whole ring softens together
 * and the lifted slice matches its neighbours instead of being the only rounded one.
 */
const DONUT_CORNER_RADIUS = 6;

/** Just enough of a gap between bands for the rounded ends to be visible as ends. */
const DONUT_PADDING_ANGLE = 2;

/** Matches the centre count-up, so the ring and the total settle together. */
const DONUT_SWEEP_MS = 700;

/**
 * Once the reader is pointing at slices the centre figure is feedback, not an entrance —
 * it re-settles inside the micro-interaction window instead of taking the mount sweep.
 */
const CENTRE_SWAP_MS = 900;

/**
 * A long-tailed ease-out (quint) so the highlight glides to rest instead of stopping dead.
 * The duration here covers the release; growing takes longer still, below.
 */
const HIGHLIGHT_TRANSITION =
  "transition-transform duration-[800ms] [transition-timing-function:cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none";

/**
 * Growing is the moment worth watching, so it overrides the base duration and outlasts the
 * release. Both are far past the usual hover-feedback window on purpose: this highlight is
 * something to follow rather than an acknowledgement, so it is paced to be watched.
 * The slice lift is capped at 6% because the chart only keeps a 5px margin around the ring.
 */
const SLICE_HIGHLIGHT_ACTIVE = "scale-[1.06] duration-[1000ms]";
const SWATCH_HIGHLIGHT_ACTIVE = "scale-150 duration-[1000ms]";

export interface CreativeHealthDonutProps {
  /** Scored creatives in scope split by band; the total is their sum (R18). */
  bands: InsightsBandSplit;
  className?: string;
}

/**
 * Creative health as a donut: every scored creative in scope split into Strong, Needs
 * review and At risk, with the total in the centre (R18). The chart itself is decorative —
 * the centred total and the legend carry the counts and shares as text, so the split is
 * readable without seeing the geometry.
 *
 * Pointing at a slice lifts it out of the ring and swaps the centre to that band's count.
 * That is an enhancement only: the legend already lists every count and share, so nothing
 * is hover-only for touch or keyboard readers.
 */
export function CreativeHealthDonut({
  bands,
  className,
}: CreativeHealthDonutProps) {
  const { t } = useTranslation();
  const [hoveredBand, setHoveredBand] = useState<PreflightScoreBand | null>(
    null,
  );
  /** Distinguishes the mount count-up from later hover swaps without re-rendering on hover exit. */
  const hasHoveredRef = useRef(false);

  const slices = DONUT_BANDS.map((band) => ({
    band,
    tone: bandTone(band),
    count: bands[band],
  }));
  const total = slices.reduce((sum, slice) => sum + slice.count, 0);
  const hoveredSlice = slices.find((slice) => slice.band === hoveredBand);
  /**
   * A single band fills the whole ring, so it has no ends to round or separate — rounding one
   * anyway pinches a notch into the circle where its start meets its finish.
   */
  const hasSeparateBands = slices.filter((slice) => slice.count > 0).length > 1;

  const handleSliceEnter = (band: PreflightScoreBand) => {
    hasHoveredRef.current = true;
    setHoveredBand(band);
  };

  return (
    <div
      className={cn(
        "flex flex-col items-center gap-5 sm:flex-row sm:items-center sm:gap-6",
        className,
      )}
    >
      <div className="relative size-33 shrink-0">
        {total > 0 ? (
          <div aria-hidden className="absolute inset-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={slices}
                  dataKey="count"
                  nameKey="band"
                  innerRadius={DONUT_INNER_RADIUS}
                  outerRadius={DONUT_OUTER_RADIUS}
                  paddingAngle={hasSeparateBands ? DONUT_PADDING_ANGLE : 0}
                  cornerRadius={
                    hasSeparateBands && hoveredBand !== null
                      ? DONUT_CORNER_RADIUS
                      : 0
                  }
                  stroke="none"
                  isAnimationActive={canAnimate()}
                  animationDuration={DONUT_SWEEP_MS}
                  animationEasing="ease-out"
                  onMouseEnter={(_entry, index) => {
                    const slice = slices[index];
                    if (slice) handleSliceEnter(slice.band);
                  }}
                  onMouseLeave={() => setHoveredBand(null)}
                >
                  {slices.map((slice) => (
                    <Cell
                      key={slice.band}
                      fill={TONE_CHART_FILL[slice.tone]}
                      className={cn(
                        // `view-box` puts the transform origin at the chart centre — which is the
                        // donut's centre — so the slice grows outward in place rather than drifting.
                        "origin-center [transform-box:view-box]",
                        HIGHLIGHT_TRANSITION,
                        slice.band === hoveredBand && SLICE_HIGHLIGHT_ACTIVE,
                      )}
                    />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div
            aria-hidden
            className="border-muted absolute inset-0 rounded-full border-[26px]"
          />
        )}
        {/* Transparent to the pointer so the slices underneath stay hoverable. */}
        <div
          className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-0.5"
          aria-label={
            hoveredSlice
              ? t("preflight.marketOverview.donutBandAria", {
                  count: hoveredSlice.count,
                  band: t(`preflight.band.${hoveredSlice.band}`),
                  defaultValue_one: "{{count}} creative rated {{band}}",
                  defaultValue_other: "{{count}} creatives rated {{band}}",
                })
              : t("preflight.marketOverview.donutTotalAria", {
                  count: total,
                  defaultValue_one: "{{count}} scored creative in scope",
                  defaultValue_other: "{{count}} scored creatives in scope",
                })
          }
        >
          <span
            aria-hidden
            className={cn(
              "text-[26px] leading-none font-semibold tracking-tight tabular-nums transition-colors duration-[800ms]",
              hoveredSlice ? TONE_TEXT[hoveredSlice.tone] : "text-foreground",
            )}
          >
            <AnimatedNumber
              value={hoveredSlice ? hoveredSlice.count : total}
              durationMs={
                hasHoveredRef.current ? CENTRE_SWAP_MS : DONUT_SWEEP_MS
              }
            />
          </span>
          <span
            key={hoveredBand ?? "total"}
            aria-hidden
            // 68px is the widest the ring's hole allows on the label's baseline (a 69px
            // chord), so the longest band name — "Needs review" — fits without truncating.
            className="text-muted-foreground/80 motion-safe:animate-in motion-safe:fade-in max-w-17 truncate text-center text-[10px] motion-safe:duration-[800ms]"
          >
            {hoveredSlice
              ? t(`preflight.band.${hoveredSlice.band}`)
              : t("preflight.marketOverview.donutTotalLabel", "creatives")}
          </span>
        </div>
      </div>

      <div className="flex min-w-0 flex-col gap-2.5">
        <div className="flex items-center gap-1">
          <span className="text-muted-foreground font-mono text-[11px] tracking-wide uppercase">
            {t("preflight.marketOverview.donutTitle", "Creative health")}
          </span>
          <WidgetHelp topic="health" className="-my-1" />
        </div>
        <ul
          className="flex min-w-0 flex-col gap-2.5"
          aria-label={t(
            "preflight.marketOverview.donutLegendAria",
            "Creative health by band",
          )}
        >
          {slices.map((slice) => (
            <li
              key={slice.band}
              className="flex min-w-0 items-center gap-2.5"
              onMouseEnter={() => handleSliceEnter(slice.band)}
              onMouseLeave={() => setHoveredBand(null)}
            >
              <span
                aria-hidden
                className={cn(
                  "size-2.5 shrink-0 rounded-sm",
                  TONE_BG[slice.tone],
                  HIGHLIGHT_TRANSITION,
                  slice.band === hoveredBand && SWATCH_HIGHLIGHT_ACTIVE,
                )}
              />
              {/* Count leads the row so the split reads as numbers first, label second. */}
              <span
                className={cn(
                  "min-w-5 shrink-0 text-[15px] font-semibold tabular-nums",
                  slice.count > 0
                    ? TONE_TEXT[slice.tone]
                    : "text-muted-foreground",
                )}
              >
                {slice.count}
              </span>
              <span className="text-foreground min-w-0 truncate text-[13px]">
                {t(`preflight.band.${slice.band}`)}
              </span>
              <span className="text-muted-foreground shrink-0 text-xs tabular-nums">
                {t("preflight.marketOverview.bandShare", "{{value}}%", {
                  value:
                    total > 0 ? Math.round((slice.count / total) * 100) : 0,
                })}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
